/**
 * Проверка владения данными.
 *
 * Каждое обращение к пространству проверяет владельца, а не только маршрут
 * (ТЗ SEC-12). На клиенте это защищает от смешивания профилей в одном
 * браузере; серверная проверка выполняется независимо.
 */

import type { InstallationId, UserId } from '@type2x/shared-contracts';

import type { Workspace, WorkspaceOwner } from '../contracts/workspace.js';

/** Ошибка доступа к чужому пространству. */
export class OwnershipError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'OwnershipError';
  }
}

/** Идентификатор пользователя или null для гостя. */
export function userOf(owner: WorkspaceOwner): UserId | null {
  return owner.kind === 'user' ? owner.userId : null;
}

/** Принадлежит ли пространство этой установке. */
export function belongsToInstallation(
  workspace: Workspace,
  installationId: InstallationId,
): boolean {
  return workspace.owner.installationId === installationId;
}

/**
 * Проверяет владение и возвращает пространство.
 *
 * @throws {OwnershipError} если установка не совпадает.
 */
export function assertOwnership(workspace: Workspace, installationId: InstallationId): Workspace {
  if (!belongsToInstallation(workspace, installationId)) {
    throw new OwnershipError('Пространство принадлежит другой установке');
  }
  return workspace;
}

/**
 * Привязывает гостевое пространство к зарегистрированному пользователю.
 *
 * Операция одноразовая: повторный вызов для уже привязанного пространства
 * отклоняется, иначе чужой прогресс можно было бы присвоить (ТЗ FR-WS-07).
 *
 * @throws {OwnershipError} если пространство уже принадлежит пользователю.
 */
export function claimForUser(workspace: Workspace, userId: UserId, now: string): Workspace {
  if (workspace.owner.kind === 'user') {
    throw new OwnershipError('Пространство уже привязано к аккаунту');
  }

  return {
    ...workspace,
    owner: { kind: 'user', userId, installationId: workspace.owner.installationId },
    updatedAt: now,
  };
}
